/* eslint-disable no-restricted-globals */
import { useEffect, MutableRefObject } from "react";

import { SUPPORT_PASSIVE_EVENT } from "../utils/support-passive-event";
import { throttle } from "../utils/throttle";
import { getElementScrollPosition } from "../utils/get-element-scroll-position";
import { TContext } from "../components/anchor-context";
import { TStore } from "../components/anchor-provider";

const PASSIVE_OPTION = SUPPORT_PASSIVE_EVENT && {
  capture: false,
  passive: true
};

export function useAnchorScrollListener(
  ref: MutableRefObject<TStore>,
  setHash: TContext["setHash"],
  offset,
  blockScrollEvent
) {
  const { sections, scroller } = ref.current;

  useEffect(() => {
    const onScroll = (event?: React.UIEvent<HTMLElement>) => {
      if (blockScrollEvent || !sections.length) {
        return;
      }

      const y =
        (scroller
          ? scroller.scrollTop
          : window.pageYOffset || document.documentElement.scrollTop) + offset;

      // Before the first element
      if (getElementScrollPosition(sections[0], scroller) > y) {
        if (event) {
          setHash("#", false);
        }
        return;
      }

      // Get first element overflowing top, get the previous one
      const selectedIndex = sections.findIndex(
        item => getElementScrollPosition(item, scroller) > y
      );

      const selectedElement =
        selectedIndex === -1
          ? sections[sections.length - 1]
          : sections[Math.max(selectedIndex - 1, 0)];

      if (selectedElement) {
        setHash(`#${selectedElement.id}`, false);
      }
    };

    const throttleScrollEvent = throttle(onScroll, 100);
    addEventListener("scroll", throttleScrollEvent, PASSIVE_OPTION);
    onScroll();

    return () => {
      removeEventListener("scroll", throttleScrollEvent, PASSIVE_OPTION);
      throttleScrollEvent.cancel();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, blockScrollEvent]);
}
